import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

export default function PatientDashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [tab, setTab] = useState('appointments');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get('/appointments/mine'),
      apiClient.get('/prescriptions/mine'),
    ])
      .then(([apptRes, presRes]) => {
        setAppointments(apptRes.data.appointments);
        setPrescriptions(presRes.data.prescriptions);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <span className="eyebrow">My Dashboard</span>
            <h1>Welcome back, {user?.fullName?.split(' ')[0]}</h1>
          </div>
          <Link to="/book" className="btn btn-primary">Book New Appointment</Link>
        </div>

        <div className="dashboard-tabs">
          <button className={`dashboard-tab ${tab === 'appointments' ? 'active' : ''}`} onClick={() => setTab('appointments')}>Appointments</button>
          <button className={`dashboard-tab ${tab === 'prescriptions' ? 'active' : ''}`} onClick={() => setTab('prescriptions')}>Prescriptions</button>
          <button className={`dashboard-tab ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>Billing</button>
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : tab === 'appointments' ? (
          <AppointmentsList appointments={appointments} />
        ) : tab === 'prescriptions' ? (
          <PrescriptionsList prescriptions={prescriptions} />
        ) : (
          <BillingList />
        )}
      </div>
    </div>
  );
}

function AppointmentsList({ appointments }) {
  if (!appointments.length) {
    return <div className="card empty-state">You have no appointments yet. <Link to="/book">Book one now.</Link></div>;
  }
  return (
    <div className="appt-list">
      {appointments.map((a) => (
        <div className="card appt-card" key={a.id}>
          <div>
            <h4>{a.Service?.name || 'Consultation'}</h4>
            <p className="appt-meta">
              {new Date(a.scheduledAt).toLocaleString()} · {a.appointmentType.replace('_', ' ')}
              {a.clinician ? ` · with ${a.clinician.fullName}` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className={`status-pill status-${a.status}`}>{a.status.replace('_', ' ')}</span>
            {a.status !== 'completed' && a.status !== 'cancelled' && (
              <Link to={`/intake/${a.id}`} className="btn btn-outline" style={{ color: 'var(--color-primary)', borderColor: 'var(--color-primary)', padding: '8px 16px' }}>
                AI Intake
              </Link>
            )}
            {a.status === 'confirmed' && (
              <Link to={`/session/${a.id}`} className="btn btn-primary" style={{ padding: '8px 16px' }}>
                Join Session
              </Link>
            )}
            {a.paymentStatus === 'unpaid' && a.Service && (
              <Link
                to={`/pay/${a.id}`}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Pay {a.Service.currency} {a.Service.price}
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function BillingList() {
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingPdf, setOpeningPdf] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      apiClient.get('/billing/invoices'),
      apiClient.get('/billing/receipts'),
    ])
      .then(([invoiceRes, receiptRes]) => {
        setInvoices(invoiceRes.data.invoices || []);
        setReceipts(receiptRes.data.receipts || []);
      })
      .catch((err) => {
        setError(
          err.response?.data?.message ||
            'Unable to load your billing documents.'
        );
      })
      .finally(() => setLoading(false));
  }, []);

  async function openPdf(type, id) {
    const key = `${type}-${id}`;
    setOpeningPdf(key);
    setError('');

    try {
      const res = await apiClient.get(`/billing/${type}s/${id}/pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');

      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          `Unable to open the ${type} PDF.`
      );
    } finally {
      setOpeningPdf('');
    }
  }

  if (loading) {
    return <p>Loading billing documents…</p>;
  }

  if (error && !invoices.length && !receipts.length) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!invoices.length && !receipts.length) {
    return (
      <div className="card empty-state">
        No invoices or receipts are available yet.
      </div>
    );
  }

  return (
    <div className="appt-list">
      {error && <div className="alert alert-error">{error}</div>}

      {invoices.map((invoice) => {
        const key = `invoice-${invoice.id}`;

        return (
          <div className="card appt-card" key={key}>
            <div>
              <h4>Invoice {invoice.invoiceNumber}</h4>
              <p className="appt-meta">
                {invoice.description} · {invoice.currency} {invoice.totalAmount} ·{' '}
                {new Date(invoice.issueDate).toLocaleDateString()}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`status-pill status-${invoice.status}`}>
                {invoice.status}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => openPdf('invoice', invoice.id)}
                disabled={openingPdf === key}
              >
                {openingPdf === key ? 'Opening…' : 'View PDF'}
              </button>
            </div>
          </div>
        );
      })}

      {receipts.map((receipt) => {
        const key = `receipt-${receipt.id}`;

        return (
          <div className="card appt-card" key={key}>
            <div>
              <h4>Receipt {receipt.receiptNumber}</h4>
              <p className="appt-meta">
                {receipt.description} · {receipt.currency} {receipt.amount} ·{' '}
                {new Date(receipt.receiptDate).toLocaleDateString()}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`status-pill status-${receipt.status}`}>
                {receipt.status}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => openPdf('receipt', receipt.id)}
                disabled={openingPdf === key}
              >
                {openingPdf === key ? 'Opening…' : 'View PDF'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PrescriptionsList({ prescriptions }) {
  if (!prescriptions.length) {
    return <div className="card empty-state">No prescriptions on file yet.</div>;
  }
  return (
    <div className="appt-list">
      {prescriptions.map((p) => (
        <div className="card appt-card" key={p.id}>
          <div>
            <h4>Prescription — {new Date(p.createdAt).toLocaleDateString()}</h4>
            <p className="appt-meta">
              {p.medications.map((m) => m.name).join(', ')}
            </p>
          </div>
          <span className={`status-pill status-confirmed`}>{p.status.replace('_', ' ')}</span>
        </div>
      ))}
    </div>
  );
}
